import React, {
  ReactNode,
  SelectHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Icon } from '../Layout/Icon'
import { Spinner } from '../Spinner'

type Option = {
  label?: string
  labels?: (string | number)[]
  value: string | number | null
  additionalData?: {
    [key: string]: any
  }
}

type DropdownProps = {
  label?: string
  topLabels?: (string | number)[]
  value: string | number
  options: Option[]
  placeholder?: string
  showLabel?: boolean
  optionLabelRenderer?: (
    option: Option,
    label: string | number,
    index: number,
    inMainArea?: boolean,
  ) => ReactNode
  optionLabelCountInMainArea?: number
  onChange?: (selectedOption: Option) => void
  className?: string
  selectProps?: SelectHTMLAttributes<HTMLSelectElement>
  loading?: boolean
  labelWidths?: number[]
  iconClassName?: string
  mainBorderClassName?: string
  secondaryBorderClassName?: string
  helperText?: string
  topLabelsClassName?: string
  alignSelected?: string
}

const Dropdown = React.forwardRef<HTMLSelectElement, DropdownProps>(
  (props: DropdownProps, selectRef) => {
    const {
      label,
      topLabels,
      value,
      options,
      placeholder,
      alignSelected = 'right',
      showLabel = alignSelected !== 'left',
      optionLabelRenderer,
      optionLabelCountInMainArea,
      onChange,
      className = '',
      selectProps = {},
      loading = false,
      labelWidths = [],
      iconClassName,
      mainBorderClassName,
      secondaryBorderClassName,
      helperText = '',
      topLabelsClassName,
      ...otherProps
    } = props

    const selectedOption = useMemo(
      () => options.find(({ value: optionValue }) => optionValue === value),
      [value, options],
    )

    const [dropdownOpen, setDropdownOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    const maxLabels = useMemo<number>(
      () =>
        Math.max(
          ...options.map(({ labels: optionLabels }) =>
            Array.isArray(optionLabels) ? optionLabels.length : 1,
          ),
        ),
      [options],
    )

    // We calculate this based on optionLabelCountInMainArea, because even though options may have 2 labels, only
    // one of them will be shown on "selected" area. It can happen because of the optionLabelRenderer function, which
    // will ignore the cell completely if null is returned. That is why in this case we urge to provide optionLabelCountInMainArea
    // explicitely
    const columnsInMainArea = useMemo(
      () => (showLabel ? 1 : 0) + (optionLabelCountInMainArea || maxLabels) + 1,
      [optionLabelCountInMainArea, showLabel, maxLabels],
    )

    const mainCellClasses = `p-[8px] align-middle [&:not(:first-child)]:text-${alignSelected} [&:not(:first-child)]:pl-0 ${
      dropdownOpen ? 'first:rounded-tl last:rounded-tr' : 'first:rounded-l last:rounded-r'
    }`

    const getOptionCellClasses = useCallback(
      (lastOption: boolean) =>
        `Type-Large-Regular p-[8px] border-b-[1px] border-5-grey [&:not(:first-child)]:text-right [&:not(:first-child)]:pl-0 ${
          lastOption ? 'border-b-0 first:rounded-bl last:rounded-br' : ''
        } `,
      [],
    )

    const renderLabel = useCallback(
      (option: Option, optionLabel: string | number, index: number, inMainArea = false) => {
        if (optionLabelRenderer) {
          const result = optionLabelRenderer(option, optionLabel, index, inMainArea)
          // If renderer returns null, we don't print the column at all
          if (result === null) {
            return null
          }
          return inMainArea ? (
            <div key={`${optionLabel}-${index}`} className={`${mainCellClasses}`}>
              {result}
            </div>
          ) : (
            result
          )
        }
        return inMainArea ? (
          <div
            key={`${optionLabel}-${index}`}
            className={`${mainCellClasses} col-span-${labelWidths[index]}`}
          >
            {optionLabel}
          </div>
        ) : (
          optionLabel
        )
      },
      [optionLabelRenderer, mainCellClasses, labelWidths],
    )

    const handleOptionClick = useCallback(
      (option: Option) => {
        if (typeof onChange === 'function') {
          setDropdownOpen(false)
          onChange(option)
        }
      },
      [onChange],
    )

    // Close the Dropdown when outside click is detected
    useEffect(() => {
      const handleClickOutside: EventListener = (event) => {
        if (
          event.target instanceof EventTarget &&
          ref.current &&
          !ref.current.contains(event.target as Node)
        ) {
          setDropdownOpen(false)
        }
      }
      document.addEventListener('click', handleClickOutside, true)
      return () => {
        document.removeEventListener('click', handleClickOutside, true)
      }
    }, [setDropdownOpen])

    /**
     * The big issue with this component was related to keeping the column positions when dropdown
     * is open, because cells loose their position when they have position='absolute'. So the trick
     * here is that whenever the dropdown is open, we show 2 main areas
     *
     * 1. Real main area, which was shown when dropdown was closed. This will be hidden to avoid
     *    duplicates. It will be 'visibility:invisible' and not 'display:none', because we need to
     *    preserve its space.
     * 2. The cloned main area, which will be the one which user will see and interact with. Since the
     *    cloned area will have the exact dimentions like the real one and it will be together in
     *    absolute position with the cells, the cells in the dropdown will look the way we want.
     */

    return (
      <div className={`relative ${className} dropdown-${props.selectProps?.name}`} ref={ref}>
        <div>
          {Array.isArray(topLabels) && (
            <div
              className='grid'
              style={{
                gridTemplateColumns: `${labelWidths.map((n) => `${n}fr`).join(' ')} 40px`,
              }}
            >
              {topLabels?.map((l, i) => (
                <div
                  className={`md:Type-Base-Regular Type-Small-Regular p-[5px] pb-[5px] pl-0 pr-0 md:p-[10px] [&:not(:first-child)]:text-right ${topLabelsClassName}`}
                  key={l}
                >
                  {l}
                </div>
              ))}
            </div>
          )}
          <div
            role='button'
            onClick={() => setDropdownOpen(!dropdownOpen)}
            onKeyDown={() => setDropdownOpen(!dropdownOpen)}
            className={`Type-Large-Regular grid items-center justify-between ${
              dropdownOpen
                ? 'rounded-tl rounded-tr text-teal-hover'
                : 'rounded-[5px] text-tight-black'
            } ${mainBorderClassName || 'border-[1px] border-tight-black'} `}
            tabIndex={0}
            {...otherProps}
            style={{
              gridTemplateColumns: `${(labelWidths.length
                ? labelWidths
                : [...Array(columnsInMainArea - 1).keys()].map((n) => 1)
              )
                .map((n) => `${n}fr`)
                .join(' ')} 40px`,
            }}
          >
            {showLabel && <div className={mainCellClasses}>{label}</div>}
            {selectedOption?.label &&
              maxLabels === 1 &&
              renderLabel(selectedOption, selectedOption?.label, 0, true)}
            {/* The reason we have used borders and radiuses on columns is that there is no way to add border to the row itself */}
            {maxLabels > 1 &&
              Array.isArray(selectedOption?.labels) &&
              selectedOption?.labels?.map((l, i) => renderLabel(selectedOption, l, i, true))}
            {!selectedOption && <span className={mainCellClasses}>{placeholder}</span>}
            <div className={` col-span-1 align-middle text-2xl ${mainCellClasses}`}>
              {loading ? (
                <Spinner />
              ) : (
                <Icon name='expand_more' className={`!block ${iconClassName}`} />
              )}
            </div>
          </div>
          <select
            ref={selectRef}
            {...selectProps}
            value={value}
            className='hidden'
            onChange={() => {}}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value || ''}>
                {option.label}
              </option>
            ))}
          </select>
          {helperText && <p className='Type-Small-Regular mt-[3px] text-red'>{helperText}</p>}
        </div>
        {dropdownOpen && (
          <div className='Type-Large-Regular absolute z-20 inline-grid w-full'>
            <div
              className={`grid max-h-[300px] w-full overflow-y-auto rounded-bl rounded-br border-[1px] border-t-0 border-tight-black bg-pure-white  ${secondaryBorderClassName}`}
            >
              {options.map((option, index) => (
                <div
                  key={option.value}
                  className='Type-Large-Regular grid cursor-pointer grid-flow-col hover:bg-5-grey '
                  onClick={() => handleOptionClick(option)}
                  onKeyDown={() => handleOptionClick(option)}
                  role='button'
                  tabIndex={-1}
                  style={{
                    gridTemplateColumns: `${(labelWidths.length
                      ? labelWidths
                      : [...Array(columnsInMainArea - 1).keys()].map((n) => 1)
                    )
                      .map((n) => `${n}fr`)
                      .join(' ')} 40px`,
                  }}
                >
                  {label && maxLabels + 1 < columnsInMainArea && (
                    <div className={getOptionCellClasses(index === options.length - 1)} />
                  )}
                  {option.label && maxLabels === 1 && (
                    <div className={getOptionCellClasses(index === options.length - 1)}>
                      {renderLabel(option, option.label, 0)}
                    </div>
                  )}
                  {maxLabels > 1 &&
                    Array.isArray(option.labels) &&
                    option.labels.map((l, i) => (
                      <div
                        key={l}
                        className={`${getOptionCellClasses(
                          index === options.length - 1,
                        )} col-span-${labelWidths[i]}`}
                      >
                        {renderLabel(option, l, i)}
                      </div>
                    ))}
                  {maxLabels < columnsInMainArea && (
                    <div className={getOptionCellClasses(index === options.length - 1)} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  },
)

export { Dropdown }
